import React, {useCallback, useEffect, useReducer, useState} from 'react';
import styles from './FunctionalButtons.scss';

import {Service as Confirmation} from '../../services/confirmationService';
import {Print} from '../../services/printService';
import {ScheduledSpot} from '../../services/scheduledSpotService';
import {User} from '../../services/userService';

import utils from '../../utils';
import {useTranslation} from 'react-i18next';
import ScheduledSpotCancelConfirmationModal from './ScheduledSpotCancelConfirmationModal';

const {CONFIRMATION_DELAY} = utils.constants;


// 
// settle/drawdown button
// print/pdf
// murex confirmation


function doPrint(data) {
    // FIXME - second parameter which should be a function to retrieve the drawdowns if this is a Window Forward
    // will probably crash without that if we call for a WF...
    Print.generateDealDetailsPDF(data);
}

function doConfirmation(data, drawdown) {
    let theData = data;
    if (drawdown) {
        data.product = 'DR';
        // we also need to add the parent
    }
    Confirmation.open(theData);
}

// note expander is it's own column
// no print for child row
// we don't want to show the confirmation until the deal is 5 minutes old
// re-render will be triggered by data grid - so we externalize the 5 minutes
function FunctionalButtons(props) {
    const {t} = useTranslation();
    const {node, data, drawdown} = props;
    const [expanded, setExpanded] = useState(node?.expanded);
    const [showCancelScheduledSpotConfirmationModal, setShowCancelScheduledSpotConfirmationModal] = useState(false);

    const confirmScheduledSpotCancellation = () => {
        setShowCancelScheduledSpotConfirmationModal(true);
    };

    const cancelScheduledSpot = (orderId) => {
        setShowCancelScheduledSpotConfirmationModal(false);
        ScheduledSpot.cancelScheduledSpot(orderId);
    };

    const donotCancelScheduledSpot = () => {
        setShowCancelScheduledSpotConfirmationModal(false);
    };

    const amendScheduledSpot = () => {
        const selected = User.user?.companies?.find(company => company.code === data?.clientId);
        const matchedBuyAccount = selected?.buyAccounts?.find((a) => a.ssid == data?.buyAccountId);
        const matchedSellAccount = selected?.sellAccounts?.find((a) => a.ssid == data?.sellAccountId);

        const scheduledSpotToAmend = {
            clientId: data?.clientId,
            orderId: data?.orderId,
            buyCurrency: data?.buyCurrency,
            sellCurrency: data?.sellCurrency,
            buyAmount: data?.buyAmount ? ''+data?.buyAmount : undefined,
            sellAmount: data?.sellAmount ? ''+data?.sellAmount : undefined,
            buyAccount: matchedBuyAccount ? matchedBuyAccount : undefined,
            sellAccount: matchedSellAccount ? matchedSellAccount : undefined,
            valueDateDate: data?.valueDate,
            tradeMemo: data?.tradeMemo,
            wireMemo: data?.wireMemo,
            inverseQuote: data.inverseQuote,
        };
        ScheduledSpot.loadScheduledSpot(scheduledSpotToAmend);
    };

    useEffect(() => {
        // can we just look at node.expanded here?
        const expandListener = (event) => setExpanded(event?.node?.expanded);
        node?.addEventListener('expandedChanged', expandListener);
  
        return () => {
            node?.removeEventListener('expandedChanged', expandListener);
        };
    }, []);

    const hideConfirm = ((Date.now() - data?.creationTime) < CONFIRMATION_DELAY);
    // TODO Check if this is fine
    function parseTradeDateTime(str) {
        if (!str) return 0;
        // "20260429 08:21:14.107" → "2026-04-29T08:21:14.107"
        return new Date(
            str.replace(/(\d{4})(\d{2})(\d{2}) /, '$1-$2-$3T')
        ).getTime();
    }

    function isTodayBeforeExecutionDate(executionDateStr) {
        const executionTime = parseTradeDateTime(executionDateStr);
        if (!executionTime) {
            return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const executionDay = new Date(executionTime);
        executionDay.setHours(0, 0, 0, 0);

        return today.getTime() < executionDay.getTime();
    }

    const cannotChangeScheduledSpot =
        !isTodayBeforeExecutionDate(data?.executionDate)
        || data?.status === 'CANCELLED' || data?.status === 'COMPLETED' || data?.status === 'FAILED';
    // recommended way of causing a re-render
    const [, forceUpdate] = useReducer(() => ({}));

    // if we need to, set a timer to update the confirmation link
    useEffect(() => {
        if (hideConfirm) {
            setTimeout(() => {
                // trigger an update
                forceUpdate();
            }, (CONFIRMATION_DELAY - (Date.now() - data?.creationTime)));
        }
    }, []);

    const onClick = useCallback(() => node?.setExpanded(!node?.expanded), [node]);

    // first column - expander - we are either normal, unsettled, dd or fully dd.
    let className = `${styles.actionIcon} td-icon icon-small td-icon-colour-primary ${expanded ? 'td-icon-collapse' : 'td-icon-expand'  }`

    // if we're not a master (expandable) row, then add a hidden span to fix alignment
    return (
        <div className={styles.actionContainer}>
            {node?.master && (
                <span className={className} onClick={onClick} ></span>
            )}
            {drawdown || data.product === 'SCHEDULED_SPOT' ? null:
                <span className={`${styles.actionIcon} td-icon icon-small td-icon-colour-primary td-icon-pdf`} title={t('td.fx.ticket.viewprint.tooltip')} onClick={() => doPrint(data)}></span>
            }
            {data.product !== 'SCHEDULED_SPOT' && (
                <span
                    style={hideConfirm ? {visibility: 'hidden'} : null} title={t('td.fx.ticket.confirmation.tooltip')}
                    className={`${styles.actionIcon} td-icon icon-small td-icon-colour-primary td-icon-manageBusiness`}
                    onClick={() => doConfirmation(data, drawdown)}>
                </span>
            )}
            {data.product === 'SCHEDULED_SPOT' && (
                <>
                    <span
                        style={cannotChangeScheduledSpot ? {visibility: 'hidden'} : null} title={t('td.fx.ticket.amend.tooltip')}
                        className={`${styles.actionIcon} td-icon icon-small td-icon-colour-primary td-icon-edit2`}
                        onClick={amendScheduledSpot}>
                    </span>
                    <span
                        style={cannotChangeScheduledSpot ? {visibility: 'hidden'} : null} title={t('td.fx.ticket.cancel.tooltip')}
                        className={`${styles.actionIcon} td-icon icon-small td-icon-colour-primary td-icon-delete`}
                        onClick={confirmScheduledSpotCancellation}>
                    </span>
                </>
            )}
            <ScheduledSpotCancelConfirmationModal
                show={showCancelScheduledSpotConfirmationModal}
                onCancel={donotCancelScheduledSpot}
                onConfirm={cancelScheduledSpot}
                orderId={data?.orderId}
            />
        </div>
    );
}
export default FunctionalButtons;
